import {
  CALCULATOR_CLOSE_DESCRIPTION,
  CALCULATOR_CLOSE_LABEL,
  CALCULATOR_OPEN_IN_NEW_TAB_LABEL,
  RIGHT_PANEL_TAB_CATEGORY_SPEND,
  RIGHT_PANEL_TAB_FINANCIAL,
  RIGHT_PANEL_TAB_LABELS,
  RIGHT_PANEL_TAB_NUTRITION,
  RIGHT_PANEL_TAB_QUANTITY,
  RIGHT_PANEL_TAB_RESISTANCE,
  RIGHT_PANEL_TOOL_CALCULATORS,
  RIGHT_PANEL_TOOL_LABELS,
  RIGHT_PANEL_TOOL_LIVE_TOTALS,
  RIGHT_PANEL_TWO_COLUMN_MIN_WIDTH_PX,
} from 'constants/uiConstants';
import CalculatorPicker from 'components/Calculators/CalculatorPicker';
import { buildSiteNodeKey } from 'hooks/useSiteAutofill';
import {
  buildCalculatorHref,
  CalculatorId,
  getCalculator,
} from 'utils/calculators';
import { Spin, Tooltip } from 'antd';
import PlanActualSegmented from 'components/common/PlanActualSegmented';
import React, { forwardRef, Suspense, useCallback, useState } from 'react';
import { LuExternalLink, LuX } from 'react-icons/lu';
import {
  PiCalculator,
  PiChartBar,
  PiChartPie,
  PiCurrencyDollar,
  PiEye,
  PiEyeSlash,
  PiLayout,
  PiLeaf,
  PiPushPinSlash,
  PiPushPinSlashFill,
  PiRows,
  PiScales,
  PiShieldCheck,
} from 'react-icons/pi';
import { useDispatch, useSelector } from 'react-redux';
import {
  closeRightPanelTool,
  RightPanelTabId,
  RightPanelTabStatus,
  RightPanelTool,
  setRightPanelActiveCalculator,
  setRightPanelActivePinnedTab,
  setRightPanelActiveTool,
  setRightPanelCategoryFilter,
  setRightPanelChartVisible,
  setRightPanelTabOrder,
  toggleRightPanelSectionExpanded,
} from '../../redux/slices/InsightsSlice';
import { RootState } from '../../redux/Store';
import {
  getRightPanelActiveCalculator,
} from '../../redux/selectors/InsightsSelectors';
import MergeSection from './MergeSection';
import PinnedTabControl from './PinnedTabControl';
import RightPanelTabRouter from './RightPanelTabRouter';

// Only loaded once someone opens Calculators (OP-2383, OP-2585).
const CalculatorPanel = React.lazy(() => import('./panels/CalculatorPanel'));

const TOOL_OPTIONS: { value: RightPanelTool; label: string }[] = [
  RIGHT_PANEL_TOOL_LIVE_TOTALS,
  RIGHT_PANEL_TOOL_CALCULATORS,
].map((value) => ({ value, label: RIGHT_PANEL_TOOL_LABELS[value] }));

const TOOL_ICONS: Record<RightPanelTool, React.ReactElement> = {
  [RIGHT_PANEL_TOOL_LIVE_TOTALS]: <PiChartBar size={16} aria-hidden />,
  [RIGHT_PANEL_TOOL_CALCULATORS]: <PiCalculator size={16} aria-hidden />,
};

// Calculator header actions — they sit on bg-gray-bright, so hover goes to white.
const CALCULATOR_HEADER_BUTTON_CLASS =
  'inline-flex h-6 w-6 items-center justify-center rounded-[var(--radius-button)] text-gray-dark hover:bg-white hover:text-charcoal';

const TAB_ICONS: Record<RightPanelTabId, React.ReactElement> = {
  [RIGHT_PANEL_TAB_NUTRITION]: <PiLeaf size={14} />,
  [RIGHT_PANEL_TAB_FINANCIAL]: <PiCurrencyDollar size={14} />,
  [RIGHT_PANEL_TAB_CATEGORY_SPEND]: <PiChartPie size={14} />,
  [RIGHT_PANEL_TAB_QUANTITY]: <PiScales size={14} />,
  [RIGHT_PANEL_TAB_RESISTANCE]: <PiShieldCheck size={14} />,
};

// Every section's own content area is wrapped in p-4 (1rem each side).
const CONTENT_PADDING_REM = 2;
const getRootFontSize = (): number =>
  parseFloat(getComputedStyle(document.documentElement).fontSize);

interface RightPinnedPanelProps {
  width: number;
  tabs: Record<RightPanelTabId, RightPanelTabStatus>;
  tabOrder: RightPanelTabId[];
  pinnedTabIds: RightPanelTabId[];
  activePinnedTabId: RightPanelTabId | null;
  isMerged: boolean;
  mergedSectionExpanded: Record<RightPanelTabId, boolean>;
  onUnpin: (tabId: RightPanelTabId) => void;
  onUnpinAll: () => void;
  onMergeToggle: () => void;
}

/**
 * The fixed-width pinned panel column.
 *
 * Single tab pinned → content area, no tab control.
 * 2+ tabs, not merged → activePinnedTabId content + PinnedTabControl at bottom.
 * 2+ tabs, merged → MergeSection list filling the content area.
 *
 * Merge/unmerge toggle appears in the header whenever 2+ tabs are pinned.
 *
 * The tool tabs (OP-2383) switch between the live totals tabs above and the
 * calculators, which take over the content area; the header's picker chooses
 * the calculator (OP-2585). Each calculator opened stays mounted (hidden)
 * while the panel exists, so switching tool or calculator and returning keeps
 * its inputs.
 * Forwarded ref lets RightPanelShell reassert DOM width via useLayoutEffect.
 */
const RightPinnedPanel = forwardRef<HTMLDivElement, RightPinnedPanelProps>(
  (
    {
      width,
      tabOrder,
      pinnedTabIds,
      activePinnedTabId,
      isMerged,
      mergedSectionExpanded,
      onUnpin,
      onUnpinAll,
      onMergeToggle,
    },
    ref
  ) => {
    const dispatch = useDispatch();
    const chartVisible = useSelector(
      (state: RootState) => state.insightsReducer.rightPanel.chartVisible
    );
    const categoryFilter = useSelector(
      (state: RootState) => state.insightsReducer.rightPanel.categoryFilter
    );
    const activeTool = useSelector(
      (state: RootState) => state.insightsReducer.rightPanel.activeTool
    );
    const activeCalculator = useSelector(getRightPanelActiveCalculator);
    const isCalculators = activeTool === RIGHT_PANEL_TOOL_CALCULATORS;
    // Farm/paddock in scope — autofills the calculator, and travels to the
    // new tab in the same `node` URL param /farms uses.
    const selectedFarmId = useSelector(
      (state: RootState) => state.farmReducer.selectedFarmId
    );
    const selectedPaddockId = useSelector(
      (state: RootState) => state.farmReducer.selectedPaddockId
    );
    const siteNodeKey = buildSiteNodeKey(selectedFarmId, selectedPaddockId);
    const calculatorHref = buildCalculatorHref(activeCalculator, siteNodeKey);
    const calculatorName = getCalculator(activeCalculator).name;
    const [visitedCalculators, setVisitedCalculators] = useState<
      CalculatorId[]
    >(isCalculators ? [activeCalculator] : []);
    if (isCalculators && !visitedCalculators.includes(activeCalculator)) {
      setVisitedCalculators([...visitedCalculators, activeCalculator]);
    }
    const hasMultiple = pinnedTabIds.length >= 2;

    // Every pinned/merged section shares this exact content width, so one
    // check here predicts whether ANY of them would render as narrow — the
    // shared control only has an effect (a single-column fallback to pick
    // Plan or Actual for) when that's the case.
    const isWide =
      (width - CONTENT_PADDING_REM) * getRootFontSize() >=
      RIGHT_PANEL_TWO_COLUMN_MIN_WIDTH_PX;

    // ── Handlers ──────────────────────────────────────────────────────────────

    const handleTabSelect = useCallback(
      (tabId: RightPanelTabId) => {
        dispatch(setRightPanelActivePinnedTab(tabId));
      },
      [dispatch]
    );

    /**
     * PinnedTabControl emits a new order of the pinned tabs on screen.
     * Map it back into the full tabOrder, keeping every other tab — not
     * pinned, or hidden by a tasks grid filter — in place.
     */
    const handleReorder = useCallback(
      (newPinnedOrder: RightPanelTabId[]) => {
        const pinnedPositions = tabOrder
          .map((id, idx) => ({ id, idx }))
          .filter(({ id }) => newPinnedOrder.includes(id))
          .map(({ idx }) => idx);

        const newTabOrder = [...tabOrder];
        pinnedPositions.forEach((pos, i) => {
          newTabOrder[pos] = newPinnedOrder[i];
        });

        dispatch(setRightPanelTabOrder(newTabOrder));
      },
      [dispatch, tabOrder]
    );

    const handleSectionToggle = useCallback(
      (tabId: RightPanelTabId) => {
        dispatch(toggleRightPanelSectionExpanded(tabId));
      },
      [dispatch]
    );

    const handleMoveSection = useCallback(
      (tabId: RightPanelTabId, direction: 'up' | 'down') => {
        const fromIdx = pinnedTabIds.indexOf(tabId);
        const toIdx = direction === 'up' ? fromIdx - 1 : fromIdx + 1;
        if (toIdx < 0 || toIdx >= pinnedTabIds.length) return;

        const newPinned = [...pinnedTabIds];
        [newPinned[fromIdx], newPinned[toIdx]] = [
          newPinned[toIdx],
          newPinned[fromIdx],
        ];
        handleReorder(newPinned);
      },
      [pinnedTabIds, handleReorder]
    );

    // In non-merged mode the header reflects the active tab.
    // In merged mode it uses a generic label.
    const headerTabId = isMerged || isCalculators ? null : activePinnedTabId;

    // Used by the merged-mode Show/Hide all charts button.
    const allChartsVisible = pinnedTabIds.every((id) => chartVisible[id]);

    return (
      <div
        ref={ref}
        style={{ width: `${width}rem` }}
        className="flex flex-shrink-0 flex-col overflow-hidden border-l bg-white pt-16"
      >
        {/* ── Tool tabs (OP-2383) ────────────────────────────────────────────
            Full-width tabs rather than an inline control, so switching
            between live totals and a calculator reads as navigation. Both tabs
            share one style; only the active state differs. */}
        <div
          role="tablist"
          aria-label="Right panel tool"
          className="flex border-b border-gray-argent bg-gray-smoke"
        >
          {TOOL_OPTIONS.map(({ value, label }) => {
            const isActive = activeTool === value;
            return (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => dispatch(setRightPanelActiveTool(value))}
                className={[
                  'relative flex h-10 min-w-0 flex-1 items-center justify-center gap-1.5 px-2 text-sm font-semibold transition-colors',
                  isActive
                    ? 'bg-white text-charcoal after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:bg-success'
                    : 'text-gray-dark hover:bg-gray-bright hover:text-charcoal',
                ].join(' ')}
              >
                {TOOL_ICONS[value]}
                <span className="truncate">{label}</span>
              </button>
            );
          })}
        </div>

        {/* ── Header ────────────────────────────────────────────────────────── */}
        <div
          className={`flex items-center justify-between gap-2 border-b px-3 py-2 ${isCalculators ? 'border-gray-argent bg-gray-bright' : 'bg-gray-50'}`}
        >
          {isCalculators ? (
            <CalculatorPicker
              size="small"
              className="flex-1"
              value={activeCalculator}
              onChange={(id) => dispatch(setRightPanelActiveCalculator(id))}
            />
          ) : headerTabId ? (
            <div
              className="flex min-w-0 cursor-pointer select-none items-center gap-1.5"
              onDoubleClick={() => onUnpin(headerTabId)}
            >
              {TAB_ICONS[headerTabId]}
              <span className="truncate text-xs font-semibold uppercase tracking-wider text-charcoal">
                {RIGHT_PANEL_TAB_LABELS[headerTabId]}
                {headerTabId === RIGHT_PANEL_TAB_FINANCIAL && (
                  <span className="ml-1 font-semibold normal-case tracking-normal text-secondary-dark">
                    (Plan data only)
                  </span>
                )}
              </span>
            </div>
          ) : (
            <span className="truncate text-sm text-gray-dark">
              All pinned panels
            </span>
          )}

          {isCalculators ? (
            <div className="flex shrink-0 items-center gap-1">
              <Tooltip title={CALCULATOR_OPEN_IN_NEW_TAB_LABEL}>
                <a
                  href={calculatorHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${CALCULATOR_OPEN_IN_NEW_TAB_LABEL} (${calculatorName})`}
                  className={CALCULATOR_HEADER_BUTTON_CLASS}
                >
                  <LuExternalLink size={14} aria-hidden />
                </a>
              </Tooltip>
              <Tooltip title={<>{CALCULATOR_CLOSE_LABEL}<br /><span className="opacity-75">{CALCULATOR_CLOSE_DESCRIPTION}</span></>}>
                <button
                  type="button"
                  aria-label={CALCULATOR_CLOSE_LABEL}
                  onClick={() => dispatch(closeRightPanelTool())}
                  className={CALCULATOR_HEADER_BUTTON_CLASS}
                >
                  <LuX size={14} aria-hidden />
                </button>
              </Tooltip>
            </div>
          ) : (
          <div className="flex items-center gap-1">
            {/* Merge toggle — only when 2+ tabs are pinned */}
            {hasMultiple && (
              <Tooltip
                title={
                  isMerged ? (
                    <>Unmerge panels<br /><span className="opacity-75">Show each panel as its own tab</span></>
                  ) : (
                    <>Merge panels<br /><span className="opacity-75">Stack all pinned panels into one view</span></>
                  )
                }
              >
                <button
                  type="button"
                  aria-label={isMerged ? 'Unmerge panels' : 'Merge panels'}
                  onClick={onMergeToggle}
                  className="inline-flex h-6 w-6 items-center justify-center rounded-[var(--radius-button)] text-secondary-dark hover:bg-gray-bright active:bg-gray-200"
                >
                  {isMerged ? <PiLayout size={14} /> : <PiRows size={14} />}
                </button>
              </Tooltip>
            )}

            {/* Show / hide chart */}
            {isMerged && hasMultiple ? (
              <Tooltip
                title={
                  allChartsVisible ? (
                    <>Hide all charts<br /><span className="opacity-75">Collapse charts in all merged panels</span></>
                  ) : (
                    <>Show all charts<br /><span className="opacity-75">Display charts in all merged panels</span></>
                  )
                }
              >
                <button
                  type="button"
                  aria-label={allChartsVisible ? 'Hide all charts' : 'Show all charts'}
                  onClick={() =>
                    pinnedTabIds.forEach((id) =>
                      dispatch(setRightPanelChartVisible({ tabId: id, visible: !allChartsVisible }))
                    )
                  }
                  className="inline-flex h-6 w-6 items-center justify-center rounded-[var(--radius-button)] text-secondary-dark hover:bg-gray-bright active:bg-gray-200"
                >
                  {allChartsVisible ? <PiEyeSlash size={14} /> : <PiEye size={14} />}
                </button>
              </Tooltip>
            ) : (
              headerTabId && (
                <Tooltip
                  title={
                    chartVisible[headerTabId] ? (
                      <>Hide chart<br /><span className="opacity-75">Collapse the chart for this panel</span></>
                    ) : (
                      <>Show chart<br /><span className="opacity-75">Display the chart for this panel</span></>
                    )
                  }
                >
                  <button
                    type="button"
                    aria-label={chartVisible[headerTabId] ? 'Hide chart' : 'Show chart'}
                    onClick={() =>
                      dispatch(
                        setRightPanelChartVisible({
                          tabId: headerTabId,
                          visible: !chartVisible[headerTabId],
                        })
                      )
                    }
                    className="inline-flex h-6 w-6 items-center justify-center rounded-[var(--radius-button)] text-secondary-dark hover:bg-gray-bright active:bg-gray-200"
                  >
                    {chartVisible[headerTabId] ? (
                      <PiEyeSlash size={14} />
                    ) : (
                      <PiEye size={14} />
                    )}
                  </button>
                </Tooltip>
              )
            )}

            {/* Unpin all (merged) or unpin active tab (non-merged) */}
            {isMerged && hasMultiple ? (
              <Tooltip title={<>Unpin all<br /><span className="opacity-75">Collapse all panels to the edge strip</span></>}>
                <button
                  type="button"
                  aria-label="Unpin all"
                  onClick={onUnpinAll}
                  className="inline-flex h-6 w-6 items-center justify-center rounded-[var(--radius-button)] text-secondary-dark hover:bg-gray-bright active:bg-gray-200"
                >
                  <PiPushPinSlashFill size={14} />
                </button>
              </Tooltip>
            ) : (
              activePinnedTabId && (
                <Tooltip title={<>Unpin panel<br /><span className="opacity-75">Return this panel to the edge strip</span></>}>
                  <button
                    type="button"
                    aria-label="Unpin panel"
                    onClick={() => onUnpin(activePinnedTabId)}
                    className="inline-flex h-6 w-6 items-center justify-center rounded-[var(--radius-button)] text-secondary-dark hover:bg-gray-bright active:bg-gray-200"
                  >
                    <PiPushPinSlash size={14} />
                  </button>
                </Tooltip>
              )
            )}
          </div>
          )}
        </div>

        {/* ── Shared Plan/Actual control ───────────────────────────────────────
            One control for every pinned/merged section (OP-2333) — narrow
            sections read this instead of each carrying its own toggle. Only
            shown when the panel is actually narrow enough to need it — at
            full width every section already shows Plan and Actual side by
            side, so the control would have nothing to do. */}
        {!isWide && !isCalculators && (
          <div className="flex items-center justify-between border-b bg-white px-3 py-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-muted">
              Category
            </span>
            <PlanActualSegmented
              value={categoryFilter}
              onChange={(cat) => dispatch(setRightPanelCategoryFilter(cat))}
            />
          </div>
        )}

        {/* ── Content area ──────────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto">
          {visitedCalculators.map((id) => (
            <div
              key={id}
              className={
                isCalculators && id === activeCalculator
                  ? 'min-h-full bg-gray-bright p-4'
                  : 'hidden'
              }
            >
              <Suspense
                fallback={
                  <div className="flex justify-center py-8">
                    <Spin />
                  </div>
                }
              >
                <CalculatorPanel calculatorId={id} siteNodeKey={siteNodeKey} />
              </Suspense>
            </div>
          ))}
          {isCalculators ? null : isMerged && hasMultiple ? (
            /* Merged mode: all pinned tabs as collapsible sections */
            pinnedTabIds.map((tabId, idx) => (
              <MergeSection
                key={tabId}
                tabId={tabId}
                isExpanded={mergedSectionExpanded[tabId]}
                isFirst={idx === 0}
                isLast={idx === pinnedTabIds.length - 1}
                onToggle={() => handleSectionToggle(tabId)}
                onMoveUp={() => handleMoveSection(tabId, 'up')}
                onMoveDown={() => handleMoveSection(tabId, 'down')}
              >
                <div className="p-4">
                  <RightPanelTabRouter tabId={tabId} />
                </div>
              </MergeSection>
            ))
          ) : (
            /* Single or non-merged: active tab content */
            <div className="p-4">
              {activePinnedTabId && (
                <RightPanelTabRouter tabId={activePinnedTabId} />
              )}
            </div>
          )}
        </div>

        {/* ── Tab switcher — only when 2+ pinned and not merged ─────────────── */}
        {hasMultiple && !isMerged && !isCalculators && (
          <PinnedTabControl
            pinnedTabIds={pinnedTabIds}
            activePinnedTabId={activePinnedTabId}
            panelWidth={width}
            onTabSelect={handleTabSelect}
            onUnpin={onUnpin}
            onReorder={handleReorder}
          />
        )}
      </div>
    );
  }
);

RightPinnedPanel.displayName = 'RightPinnedPanel';

export default RightPinnedPanel;
